/**
 * M8 Booking · Date & slot picker.
 * Fetches /api/booking/availability?month=YYYY-MM and lets the customer
 * choose one (date, slot). Holds the slot for 15 minutes via
 * POST /api/booking/hold-slot. Shows a live countdown so the customer
 * knows they must finish payment within the hold window.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Loader2, Timer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtMonth(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

export default function BookingDateSlotPicker({
    packageId, filmingLocation, customerEmail, customerName,
    isCn = false, onHoldReady, onReleaseHold, disabled = false,
}) {
    const [month, setMonth] = useState(() => { const d = new Date(); return fmtMonth(d); });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selDate, setSelDate] = useState("");
    const [selSlot, setSelSlot] = useState("");
    const [hold, setHold] = useState(null); // { hold_id, expires_at }
    const [holdErr, setHoldErr] = useState("");
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setData(null);
        axios.get(`${API}/booking/availability?month=${month}&package_id=${encodeURIComponent(packageId || "")}&location=${encodeURIComponent(filmingLocation || "")}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setData({ days: [], _error: true }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [month, packageId, filmingLocation]);

    useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

    const expiresMs = hold ? new Date(hold.expires_at).getTime() : 0;
    const remainingSec = Math.max(0, Math.floor((expiresMs - now) / 1000));
    const remainingLabel = `${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, "0")}`;

    // Auto-release hold if it expires while sitting here.
    useEffect(() => {
        if (hold && remainingSec === 0) {
            if (onReleaseHold) onReleaseHold();
            setHold(null);
            setHoldErr(isCn ? "档期暂留已过期,请重新选择。" : "Your hold expired — please pick again.");
        }
    }, [hold, remainingSec, isCn, onReleaseHold]);

    const changeMonth = (delta) => {
        const [y, m] = month.split("-").map(Number);
        const d = new Date(y, m - 1 + delta, 1);
        setMonth(fmtMonth(d));
        setSelDate(""); setSelSlot(""); setHold(null); setHoldErr("");
        if (onReleaseHold) onReleaseHold();
    };

    const createHold = async (dStr, slotId) => {
        if (!customerEmail) {
            setHoldErr(isCn ? "请先输入邮箱" : "Please enter your email first");
            return;
        }
        setHoldErr("");
        // If a different slot is currently held, release it first so the
        // user can switch morning ↔ afternoon without closing the modal.
        if (hold?.hold_id && (selDate !== dStr || selSlot !== slotId)) {
            try { await axios.delete(`${API}/booking/hold-slot/${hold.hold_id}`); } catch { /* ignore */ }
            setHold(null);
            if (onReleaseHold) onReleaseHold();
        }
        const client_token = `${customerEmail}-${dStr}-${slotId}-${packageId}`;
        try {
            const r = await axios.post(`${API}/booking/hold-slot`, {
                package_id: packageId,
                shoot_date: dStr,
                time_slot: slotId,
                customer_email: customerEmail,
                customer_name: customerName || null,
                filming_location: filmingLocation || null,
                client_token,
            }, { timeout: 10000 });
            setHold(r.data);
            onHoldReady?.(r.data, { shoot_date: dStr, time_slot: slotId });
            setSelDate(dStr); setSelSlot(slotId);
        } catch (err) {
            const msg = err?.response?.data?.detail || (isCn ? "无法暂留档期,请重试" : "Could not hold the slot — please retry");
            setHoldErr(msg);
            setHold(null);
            setSelDate(""); setSelSlot("");
        }
    };

    const grid = useMemo(() => {
        if (!data?.days) return [];
        const first = data.days[0];
        if (!first) return [];
        // Fill leading blanks so weekday 0 (Mon) sits in column 0
        const firstWd = first.weekday;
        const pre = Array(firstWd).fill(null);
        return [...pre, ...data.days];
    }, [data]);

    const weekdayHeaders = isCn ? ["一", "二", "三", "四", "五", "六", "日"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
        <div className="space-y-3" data-testid="booking-date-picker">
            <div className="flex items-center justify-between">
                <button type="button" onClick={() => changeMonth(-1)} disabled={disabled} data-testid="booking-date-prev"
                    className="h-9 w-9 rounded-full border border-cafe-espresso/10 bg-white flex items-center justify-center hover:bg-cafe-olive/5">
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="text-sm font-semibold text-cafe-espresso tabular-nums" data-testid="booking-date-month">{month}</div>
                <button type="button" onClick={() => changeMonth(1)} disabled={disabled} data-testid="booking-date-next"
                    className="h-9 w-9 rounded-full border border-cafe-espresso/10 bg-white flex items-center justify-center hover:bg-cafe-olive/5">
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>

            {loading && (
                <div className="h-40 flex items-center justify-center text-cafe-mocha text-sm gap-2" data-testid="booking-date-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> {isCn ? "正在读取档期…" : "Loading availability…"}
                </div>
            )}

            {!loading && data?.days && (
                <div>
                    <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest text-cafe-mocha/70 mb-1">
                        {weekdayHeaders.map((w) => <div key={w} className="text-center">{w}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {grid.map((day, i) => {
                            if (!day) return <div key={`e-${i}`} className="h-10" />;
                            const anyOpen = day.slots.some((s) => s.available);
                            const picked = selDate === day.date;
                            return (
                                <button key={day.date} type="button" disabled={!anyOpen || disabled}
                                    onClick={() => { setSelDate(day.date); setSelSlot(""); }}
                                    data-testid={`booking-date-${day.date}`}
                                    className={`h-10 rounded-lg text-sm font-medium tabular-nums transition-colors
                                        ${anyOpen ? "bg-white border border-cafe-espresso/10 text-cafe-espresso hover:border-cafe-olive hover:bg-cafe-olive/5"
                                            : "bg-cafe-espresso/[0.03] text-cafe-espresso/30 cursor-not-allowed"}
                                        ${picked ? "ring-2 ring-cafe-olive border-cafe-olive bg-cafe-olive/10" : ""}`}>
                                    {Number(day.date.slice(-2))}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {selDate && data?.days && (
                <div className="pt-2">
                    <div className="text-xs font-semibold text-cafe-mocha mb-2" data-testid="booking-slot-label">
                        {isCn ? `${selDate} 可选时段` : `Pick a time slot on ${selDate}`}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {(data.days.find((d) => d.date === selDate)?.slots || []).map((s) => {
                            const label = isCn ? s.label_cn : s.label_en;
                            const chosen = selSlot === s.id && hold;
                            return (
                                <button key={s.id} type="button" disabled={!s.available || disabled}
                                    data-testid={`booking-slot-${s.id}`}
                                    onClick={() => createHold(selDate, s.id)}
                                    className={`rounded-xl p-3 text-sm font-medium border transition-colors text-left
                                        ${s.available ? (chosen ? "bg-cafe-olive text-cafe-cream border-cafe-olive"
                                            : "bg-white border-cafe-espresso/10 hover:border-cafe-olive text-cafe-espresso")
                                            : "bg-cafe-espresso/[0.03] border-cafe-espresso/5 text-cafe-espresso/30"}`}>
                                    <div className="text-[11px] uppercase tracking-wider mb-0.5">{isCn ? "时段" : "Slot"}</div>
                                    <div>{label}</div>
                                    {!s.available && <div className="text-[10px] mt-0.5 text-cafe-terracotta">{isCn ? "已满" : "Booked"}</div>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {hold && (
                <div className="flex items-center gap-2 text-xs text-cafe-olive bg-cafe-olive/5 border border-cafe-olive/20 rounded-xl p-3" data-testid="booking-hold-timer">
                    <Timer className="h-3.5 w-3.5" />
                    <span>
                        {isCn
                            ? `档期已暂留 · ${remainingLabel} 内完成付款,否则自动释放。`
                            : `Slot held · finish payment within ${remainingLabel} or it will be released.`}
                    </span>
                </div>
            )}

            {holdErr && <div className="text-xs text-cafe-terracotta" data-testid="booking-hold-error">{holdErr}</div>}
        </div>
    );
}
